import * as Popover from "@radix-ui/react-popover";
import { useState } from "react";
import { Check, Percent } from "lucide-react";
import { cn } from "@/lib/cn";
import { useRateClasses } from "@/features/data";

interface RateClassPickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  compact?: boolean;
}

/** RateClassPicker renders nothing while the workspace has no rate classes. */
export function RateClassPicker({ value, onChange, compact }: RateClassPickerProps) {
  const { data: classes = [] } = useRateClasses();
  const [open, setOpen] = useState(false);

  const selected = classes.find((c) => c.id === value) ?? null;
  if (classes.length === 0) return null;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-2 rounded-md border border-line px-2.5 text-sm transition-colors hover:border-line-strong",
            compact ? "h-8" : "h-9",
            selected ? "text-ink" : "text-faint",
          )}
        >
          <Percent className="size-4" />
          <span className="max-w-32 truncate">{selected ? selected.name : "Rate"}</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={6} className="z-50 w-64 overflow-hidden rounded-lg border border-line bg-elevated shadow-xl">
          <div className="max-h-64 overflow-auto py-1">
            <Row
              active={value === null}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <span className="text-faint">Standard rate</span>
            </Row>
            {classes.map((c) => (
              <Row
                key={c.id}
                active={value === c.id}
                onClick={() => {
                  onChange(c.id);
                  setOpen(false);
                }}
              >
                <span className="truncate text-ink">{c.name}</span>
                <span className="mono text-xs text-faint">×{(c.multiplier_percent / 100).toFixed(2)}</span>
              </Row>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function Row({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface">
      {children}
      {active && <Check className="ml-auto size-4 text-accent" />}
    </button>
  );
}
